import { Json } from './database';
// import { z } from "zod"; // Temporarily commented out
import { Database } from './database';

export interface ProductSize {
  id: string;
  size_name: string;
  price: number;
  is_default: boolean;
  is_active: boolean;
  main_image_url: string | null;
  gallery_image_urls: string[] | null;
  use_main_product_image: boolean;
}

export interface Store {
  id: string;
  store_name: string;
  description?: string;
  suburb?: string;
  state?: string;
  florist_id: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  delivery_radius: number;
  delivery_days: string[];
  delivery_fee: number;
  minimum_order_amount: number;
  same_day_enabled: boolean;
  delivery_cutoff_times: {
    [key: string]: string;
  };
  store_status: 'active' | 'inactive';
  approval_status: 'pending' | 'approved' | 'rejected';
}

export interface ProductVariant {
  id: string;
  size: string;
  size_name?: string;
  price: number;
  sale_price?: number | null;
  description?: string;
  main_image_url?: string;
  is_active?: boolean;
}

export interface Product {
  id: string;
  title: string;
  price: number;
  sale_price?: number | null;
  images: string[];
  description?: string;
  selectedVariant?: {
    id: string;
    size: string;
    size_name?: string;
    price: number;
    is_active: boolean;
  } | null;
  category?: string;
  occasions?: string[];
  florist?: {
    id: string;
    store_name: string;
    suburb?: string;
    state?: string;
    coordinates?: Coordinates;
    storefront_image_url?: string;
    cover_image_url?: string;
    logo_url?: string;
  };
  store?: {
    id: string;
    store_name: string;
    suburb?: string;
    state?: string;
    coordinates?: Coordinates;
    geocoded_address?: any;
    delivery_radius?: number;
    storefront_image_url?: string;
    cover_image_url?: string;
    logo_url?: string;
    calculated_distance?: number;
  };
  uniqueId?: string;
}

export interface ProductImage {
  url: string;
  alt: string;
  is_primary: boolean;
}

export interface Size {
  id: string;
  name: string;
  price: string;
  images?: ProductImage[];
  uses_default_images?: boolean;
}

export interface ProductFormData {
  id?: string;
  title: string;
  description: string;
  category: typeof PRODUCT_CATEGORIES[number];
  active: boolean;
  occasions: typeof PRODUCT_TAGS[number][];
  sizes: {
    id?: string;
    size_name: string;
    price: number;
    image_url: string | null;
    use_product_image: boolean;
  }[];
}

export const PRODUCT_CATEGORIES = [
  "Bouquet",
  "Floral Arrangement",
  "Potted Plant",
  "Single Flower",
  "Other"
] as const;

export const PRODUCT_TAGS = [
  "Birthday",
  "Thank You",
  "Love & Romance",
  "Sympathy",
  "Just Because"
] as const;

// Temporarily comment out the Zod schema
// export const productSchema = z.object({
//   id: z.string().optional(),
//   title: z.string().min(3, "Title must be at least 3 characters"),
//   description: z.string().min(10, "Description must be at least 10 characters"),
//   category: z.enum(PRODUCT_CATEGORIES),
//   active: z.boolean(),
//   occasions: z.array(z.enum(PRODUCT_TAGS)),
//   sizes: z.array(z.object({
//     id: z.string().optional(),
//     size_name: z.string().min(1, "Size name is required"),
//     price: z.number().min(0.01, "Price must be greater than 0"),
//     image_url: z.string().nullable(),
//     use_product_image: z.boolean()
//   })).min(1, "At least one size is required")
// });

export interface ProductsTable {
  Row: {
    id: string;
    florist_id: string | null;
    title: string;
    description: string;
    price: number;
    images: string[] | null;
    active: boolean | null;
    created_at: string;
    updated_at: string;
    category: string;
    tags: string[] | null;
    is_featured: boolean | null;
    stock_level: number | null;
    sale_price: number | null;
    customization_options: Json | null;
    gallery_image_urls: string[] | null;
    main_image_url: string | null;
  };
  Insert: {
    id?: string;
    florist_id?: string | null;
    title: string;
    description: string;
    price: number;
    images?: string[] | null;
    active?: boolean | null;
    created_at?: string;
    updated_at?: string;
    category: string;
    tags?: string[] | null;
    is_featured?: boolean | null;
    stock_level?: number | null;
    sale_price?: number | null;
    customization_options?: Json | null;
    gallery_image_urls?: string[] | null;
    main_image_url?: string | null;
  };
  Update: Partial<ProductsTable['Insert']>;
}

export interface ProductSizesTable {
  Row: {
    id: string;
    created_at: string | null;
    updated_at: string | null;
    name: string;
    price: number;
    stock_level: number | null;
    images: string[] | null;
    uses_default_images: boolean | null;
    product_id: string;
    is_default: boolean | null;
  };
  Insert: {
    id?: string;
    created_at?: string | null;
    updated_at?: string | null;
    name: string;
    price: number;
    stock_level?: number | null;
    images?: string[] | null;
    uses_default_images?: boolean | null;
    product_id: string;
    is_default?: boolean | null;
  };
  Update: Partial<ProductSizesTable['Insert']>;
}

export interface StoreProfilesTable {
  Row: {
    id: string;
    florist_id: string | null;
    store_name: string;
    description: string | null;
    phone: string | null;
    email: string | null;
    business_hours: Json;
    geocoded_address: Json | null;
    delivery_radius: number | null;
    delivery_days: string[] | null;
    non_sameday_days: string[] | null;
    created_at: string | null;
    updated_at: string | null;
  };
  Insert: {
    id?: string;
    florist_id?: string | null;
    store_name: string;
    description?: string | null;
    phone?: string | null;
    email?: string | null;
    business_hours?: Json;
    geocoded_address?: Json | null;
    delivery_radius?: number | null;
    delivery_days?: string[] | null;
    non_sameday_days?: string[] | null;
    created_at?: string | null;
    updated_at?: string | null;
  };
  Update: Partial<StoreProfilesTable['Insert']>;
}

export interface ProductFilters {
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
}

export type SortableFields = {
  title: string;
  created_at: string;
};

export type ProductSortField = keyof SortableFields;

export interface ProductSort {
  field: ProductSortField;
  direction: 'asc' | 'desc';
}

export type ProductCategory = typeof PRODUCT_CATEGORIES[number];

export type ProductTag = typeof PRODUCT_TAGS[number];

export interface ProductOccasion {
  occasion_id: string;
  occasions: {
    id: string;
    name: string;
  };
}

export interface SearchProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  images: string[];
  store: {
    id: string;
    name: string;
    coordinates: { lat: number; lng: number } | null;
    suburb: string | null;
    state: string | null;
  };
  sizes: {
    id: string;
    name: string;
    price: number;
  }[];
  category: string;
  created_at: string;
  updated_at: string;
  tags: string[];
}